import { useState, useEffect, useRef } from "react";
import { Document, Page, pdfjs } from "react-pdf";


pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";


//for use in local 

// pdfjs.GlobalWorkerOptions.workerSrc = new URL(
//     "pdfjs-dist/build/pdf.worker.min.mjs",
//     import.meta.url
// ).toString();
export default function PdfModal({ documentData, onClose }) {
    const [numPages, setNumPages] = useState(null);
    const [page, setPage] = useState(1);
    const [scale, setScale] = useState(1.2);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false);

    const containerRef = useRef(null);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!numPages) return;

            if (["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;

            if (e.key === "ArrowRight") {
                setPage((p) => Math.min(p + 1, numPages));
            }

            if (e.key === "ArrowLeft") {
                setPage((p) => Math.max(p - 1, 1));
            }

            // Zoom IN
            if (
                e.key === "="
            ) {
                setScale((s) => Math.min(s + 0.2, 3));
            }

            // Zoom OUT
            if (
                e.key === "-"
            ) {
                setScale((s) => Math.max(s - 0.2, 0.6));
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [numPages]);

    useEffect(() => {
        let objectUrl;

        const fetchPdf = async () => {
            const { trialNumber, documentIdentifier } = documentData;

            setLoading(true);

            try {
                const res = await fetch(
                    `${process.env.REACT_APP_API_BASE_URL}/download_document/${trialNumber}/${documentIdentifier}`
                );

                if (!res.ok) throw new Error("Failed to fetch PDF");

                const blob = await res.blob();
                objectUrl = URL.createObjectURL(blob);
                setPdfUrl(objectUrl);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchPdf();

        return () => {
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [documentData]);

    const toggleFullscreen = async () => {
        if (!document.fullscreenElement) {
            await containerRef.current.requestFullscreen();
            setIsFullscreen(true);
        } else {
            await document.exitFullscreen();
            setIsFullscreen(false);
        }
    };

    const downloadPdf = () => {
        const link = document.createElement("a");
        link.href = pdfUrl;

        const cleanTitle =
            documentData.documentTitle?.replace(/[<>:"/\\|?*]+/g, "").trim() ||
            "document";

        link.download = cleanTitle.endsWith(".pdf")
            ? cleanTitle
            : `${cleanTitle}.pdf`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (!pdfUrl || loading) {
        return (
            <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
                <div className="bg-white px-10 py-8 rounded-2xl shadow-xl text-center">
                    <div className="animate-spin h-10 w-10 border-4 border-gray-300 border-t-[#102D33] rounded-full mx-auto mb-4" />
                    <p className="text-sm text-gray-600 font-medium">
                        Loading document...
                    </p>
                </div>
            </div>
        );
    }
    return (
        <div
            ref={containerRef}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50"
        >
            <div
                className={`
        bg-white w-full h-full md:w-[95%] md:h-[95%] 
        rounded-none md:rounded-2xl 
        shadow-2xl flex flex-col overflow-hidden
      `}
            >

                {/* ================= HEADER ================= */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between 
        bg-gradient-to-r from-[#0f2f36] to-[#174a52] 
        text-white px-4 md:px-6 py-3 md:py-4 gap-3 md:gap-0">

                    {/* LEFT */}
                    <div className="flex flex-col max-w-full md:max-w-[60%]">
                        <div className="text-xs text-gray-300">
                            Page {page} of {numPages}
                        </div>
                        <div className="text-sm font-semibold break-words leading-snug">
                            {documentData.documentTitle}
                        </div>
                    </div>

                    {/* RIGHT CONTROLS */}
                    <div className="flex flex-wrap items-center gap-2 text-xs mt-2 md:mt-0">

                        <ControlButton
                            onClick={() => setScale((s) => Math.max(s - 0.2, 0.6))}
                            label="−"
                        />

                        <div className="px-2 font-medium">
                            {Math.round(scale * 100)}%
                        </div>

                        <ControlButton
                            onClick={() => setScale((s) => Math.min(s + 0.2, 3))}
                            label="+"
                        />

                        <Divider />

                        <ControlButton
                            onClick={toggleFullscreen}
                            label={isFullscreen ? "Exit" : "Full"}
                        />

                        <ControlButton
                            onClick={downloadPdf}
                            label="Download"
                        />

                        <ControlButton
                            onClick={onClose}
                            label="Close"
                            danger
                        />
                    </div>
                </div>

                {/* ================= NAVIGATION ================= */}
                <div className="flex flex-wrap items-center justify-center gap-4 
        py-3 bg-gray-50 border-b text-xs">

                    <NavButton
                        disabled={page <= 1}
                        onClick={() => setPage((p) => p - 1)}
                        label="Prev"
                    />

                    <div className="flex items-center gap-2">
                        <input
                            type="number"
                            value={page}
                            min={1}
                            max={numPages}
                            onChange={(e) =>
                                setPage(
                                    Math.min(Math.max(1, Number(e.target.value)), numPages)
                                )
                            }
                            className="w-14 md:w-16 text-center border rounded px-2 py-1 
              focus:outline-none focus:ring-2 focus:ring-[#102D33]"
                        />

                        <span className="text-gray-600 font-medium">
                            / {numPages}
                        </span>
                    </div>

                    <NavButton
                        disabled={page >= numPages}
                        onClick={() => setPage((p) => p + 1)}
                        label="Next"
                    />
                </div>

                {/* ================= PDF VIEWER ================= */}
                <div className="flex-1 overflow-auto bg-gray-200 flex justify-center p-3 md:p-6">
                    <Document
                        file={pdfUrl}
                        onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                    >
                        <Page
                            pageNumber={page}
                            scale={window.innerWidth < 768 ? 0.9 : scale}
                            renderTextLayer={false}
                            renderAnnotationLayer={false}
                        />
                    </Document>
                </div>
            </div>
        </div>
    );
}

/* ================= COMPONENTS ================= */

function ControlButton({ onClick, label, danger }) {
    return (
        <button
            onClick={onClick}
            className={`px-3 py-2 md:px-3 md:py-1 
        rounded transition text-xs md:text-xs font-medium 
        ${danger
                    ? "bg-red-500 hover:bg-red-600 text-white"
                    : "bg-white/20 hover:bg-white/30 text-white"
                }`}
        >
            {label}
        </button>
    );
}

function NavButton({ disabled, onClick, label }) {
    return (
        <button
            disabled={disabled}
            onClick={onClick}
            className={`px-4 py-1 rounded text-xs font-medium transition ${disabled
                ? "bg-gray-300 cursor-not-allowed"
                : "bg-[#102D33] text-white hover:opacity-90"
                }`}
        >
            {label}
        </button>
    );
}

function Divider() {
    return <div className="w-px h-5 bg-white/30 mx-1" />;
}